/**
 * PriceHistoryCard — évolution des prix au m² sur 10 ans autour de la parcelle.
 *
 * Layout :
 *   - 3 KPIs : médiane actuelle, variation 12 mois, variation 10 ans.
 *   - Toggle Appartement / Maison (masqué si une seule série a des données).
 *   - Graphe composé : bande IQR (p25-p75), courbe MM4Q, points trimestriels.
 *   - Bas : bandelette de volume (nb de ventes par trimestre).
 *
 * Choix :
 *   - Pas de ligne sur la médiane brute trimestrielle (trop bruité sur 10 ans) ;
 *     on la matérialise uniquement en points sur les trimestres ayant ≥ 4 tx.
 *   - Tick d'axe X tous les 4 trimestres pour lisibilité (année seulement).
 *   - Si MM4Q et IQR sont totalement null (très faible volume), on rend un
 *     "pas assez de transactions" — pas une carte vide.
 */

import { useMemo, useState } from "react";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { FicheDoc } from "../../lib/fiches";
import { usePriceHistory } from "../../lib/env/priceHistory";
import { useAiPriceTrend } from "../../lib/ai/priceTrend";
import type {
  PriceHistory,
  PriceHistorySeries,
  PricePropertyType,
} from "../../lib/types";
import { EnvCardShell } from "./EnvCardShell";
import { Ic } from "../icons";

type Props = { fiche: FicheDoc };

export function PriceHistoryCard({ fiche }: Props) {
  const state = usePriceHistory(fiche.id, fiche.geocode);

  // Default tab based on the fiche's property type. If the fiche is a
  // Maison (ex: 330 allée des tulipes), we want to land on the Maison
  // series — the Appartement series for the same quartier would give a
  // misleading first impression of "current price". Immeuble is a building
  // that contains apartments in the DGFiP taxonomy, so we map it to
  // Appartement. Terrain or null has no meaningful preference → Appartement
  // (historically the most populated series in urban data).
  const defaultActive: PricePropertyType =
    fiche.existing?.type === "Maison" ? "Maison" : "Appartement";
  const [active, setActive] = useState<PricePropertyType>(defaultActive);

  // Switch defaults to whichever series actually has data — if the quartier
  // has only Maison transactions (typical in rural areas), don't land on
  // the empty Appartement tab.
  const autoActive = useMemo<PricePropertyType>(() => {
    if (state.status !== "ready") return active;
    const apt = findSeries(state.data, "Appartement");
    const mai = findSeries(state.data, "Maison");
    const aptCount = apt?.headline.totalTransactions ?? 0;
    const maiCount = mai?.headline.totalTransactions ?? 0;
    if (aptCount === 0 && maiCount > 0) return "Maison";
    if (maiCount === 0 && aptCount > 0) return "Appartement";
    return active;
  }, [state, active]);

  const activeSeries =
    state.status === "ready" ? findSeries(state.data, autoActive) : null;
  const activeCommuneSeries =
    state.status === "ready"
      ? findCommuneSeries(state.data, autoActive)
      : null;

  const subtitle =
    state.status === "ready"
      ? summariseHeadline(activeSeries)
      : undefined;

  return (
    <EnvCardShell
      icon={<Ic.TrendUp size={18} />}
      title="Évolution des prix au m²"
      subtitle={subtitle}
      state={state}
      footer={
        state.status === "ready" ? (
          <>
            Source : CEREMA DVF+ (DGFiP). Médiane trimestrielle lissée (MM4T
            pondérée) sur un rayon de {state.data.radius_m} m autour de la
            parcelle{state.data.radiusFallbackUsed ? " (élargi à 1 km — peu de ventes en 500 m)" : ""}.
          </>
        ) : (
          <>Source : CEREMA DVF+ (DGFiP). Rayon 500 m, fallback 1 km.</>
        )
      }
    >
      {state.status === "ready" && (
        <Body
          data={state.data}
          series={activeSeries}
          communeSeries={activeCommuneSeries}
          active={autoActive}
          onChange={setActive}
          ficheId={fiche.id}
        />
      )}
    </EnvCardShell>
  );
}

// ─── AI narration ───────────────────────────────────────────────────────

function AiNarration({ ficheId }: { ficheId: string }) {
  const ai = useAiPriceTrend(ficheId);
  // Silent on idle/loading/error : the card is fully usable without the
  // narration. We only render when Claude has produced something.
  if (ai.status !== "ready") return null;
  const { content, unavailableReason } = ai.data;
  // `unavailableReason` wins over `content` : the server sets one of the two,
  // never both meaningfully.
  const text = unavailableReason ?? content;
  if (!text) return null;
  return (
    <div className="flex items-start gap-2.5 rounded-xl bg-lime-50/60 hairline px-3.5 py-3">
      <span className="mt-0.5 shrink-0 text-lime-700">
        <Ic.Sparkle size={14} />
      </span>
      <p className="text-[13px] leading-[1.55] text-ink-800">{text}</p>
    </div>
  );
}

// ─── Header summary ─────────────────────────────────────────────────────

function summariseHeadline(series: PriceHistorySeries | null): string {
  if (!series) return "—";
  const h = series.headline;
  if (h.currentMedian == null) {
    return `${h.totalTransactions} vente${h.totalTransactions > 1 ? "s" : ""} — volume insuffisant pour une médiane fiable`;
  }
  const yoy =
    h.yoyChangePct != null
      ? ` · ${formatPctSigned(h.yoyChangePct)} sur 12 mois`
      : "";
  return `${formatEurPerM2(h.currentMedian)} actuel${yoy}`;
}

// ─── Body ───────────────────────────────────────────────────────────────

function Body({
  data,
  series,
  communeSeries,
  active,
  onChange,
  ficheId,
}: {
  data: PriceHistory;
  series: PriceHistorySeries | null;
  communeSeries: PriceHistorySeries | null;
  active: PricePropertyType;
  onChange: (p: PricePropertyType) => void;
  ficheId: string;
}) {
  const apt = findSeries(data, "Appartement");
  const mai = findSeries(data, "Maison");
  const aptCount = apt?.headline.totalTransactions ?? 0;
  const maiCount = mai?.headline.totalTransactions ?? 0;
  const bothHaveData = aptCount > 0 && maiCount > 0;

  return (
    <div className="space-y-4">
      {bothHaveData && (
        <div
          role="tablist"
          aria-label="Type de bien"
          className="inline-flex rounded-full bg-ink-50 hairline p-0.5"
        >
          {(["Appartement", "Maison"] as PricePropertyType[]).map((t) => {
            const isActive = t === active;
            const count =
              t === "Appartement" ? aptCount : maiCount;
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(t)}
                className={`h-7 px-3 rounded-full text-[12px] font-medium transition ${
                  isActive
                    ? "bg-ink-900 text-lime-300"
                    : "text-ink-600 hover:text-ink-900"
                }`}
              >
                {t}{" "}
                <span
                  className={
                    isActive ? "text-lime-300/70" : "text-ink-400"
                  }
                >
                  · {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {series ? (
        <>
          <KpiRow series={series} communeSeries={communeSeries} />
          <PriceChart series={series} communeSeries={communeSeries} communeName={data.commune.name} />
          <AiNarration ficheId={ficheId} />
        </>
      ) : (
        <EmptyState />
      )}
    </div>
  );
}

function KpiRow({
  series,
  communeSeries,
}: {
  series: PriceHistorySeries;
  communeSeries: PriceHistorySeries | null;
}) {
  const h = series.headline;
  // "vs. commune" hint on the Médiane KPI : delta du quartier par rapport à
  // la médiane actuelle commune-wide (même type de bien). Ne pas afficher si
  // on n'a pas les deux valeurs, ou si l'une d'elles est < 100 €/m² (données
  // suspectes).
  const communeMedian = communeSeries?.headline.currentMedian ?? null;
  const vsCommunePct =
    h.currentMedian != null &&
    communeMedian != null &&
    communeMedian >= 100
      ? Number((((h.currentMedian - communeMedian) / communeMedian) * 100).toFixed(0))
      : null;
  const medianHint =
    vsCommunePct != null
      ? `${formatPctSigned(vsCommunePct)} vs. commune`
      : `${h.totalTransactions} ventes sur la période`;
  return (
    <div className="grid grid-cols-3 gap-3">
      <Kpi
        label="Médiane actuelle"
        value={formatEurPerM2(h.currentMedian)}
        hint={medianHint}
      />
      <Kpi
        label="12 derniers mois"
        value={h.yoyChangePct != null ? formatPctSigned(h.yoyChangePct) : "—"}
        hint="vs. 12 mois précédents"
        tone={toneOfPct(h.yoyChangePct)}
      />
      <Kpi
        label="Depuis 10 ans"
        value={
          h.changeSinceStartPct != null
            ? formatPctSigned(h.changeSinceStartPct)
            : "—"
        }
        hint="vs. 1ʳᵉ année"
        tone={toneOfPct(h.changeSinceStartPct)}
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "pos" | "neg" | "neutral";
}) {
  const color =
    tone === "pos"
      ? "text-emerald-700"
      : tone === "neg"
        ? "text-rose-700"
        : "text-ink-900";
  return (
    <div className="rounded-xl bg-ink-50/50 hairline p-3">
      <div className="text-[11px] uppercase tracking-wide text-ink-500">
        {label}
      </div>
      <div className={`text-[17px] font-semibold tabular mt-0.5 ${color}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[11px] text-ink-500 mt-0.5 truncate">{hint}</div>
      )}
    </div>
  );
}

// ─── Chart ──────────────────────────────────────────────────────────────

type ChartRow = {
  period: string;
  year: number;
  mm4q: number | null;
  communeMm4q: number | null; // MM4Q commune-wide (même type de bien)
  p25: number | null;
  p75: number | null;
  bandLow: number | null; // p25
  bandRange: number | null; // p75 - p25, stacked on top of bandLow
  median: number | null; // quarterly median (shown as scatter dot when present)
  count: number;
};

function PriceChart({
  series,
  communeSeries,
  communeName,
}: {
  series: PriceHistorySeries;
  communeSeries: PriceHistorySeries | null;
  communeName: string;
}) {
  // Index the commune MM4Q by period so we can align with the quartier series
  // on the same x axis. Null-safe : if communeSeries is absent we just render
  // the quartier-only chart.
  const communeByPeriod = useMemo(() => {
    const m = new Map<string, number | null>();
    if (!communeSeries) return m;
    for (const p of communeSeries.points) m.set(p.period, p.median_mm4q_eur_per_m2);
    return m;
  }, [communeSeries]);

  const rows: ChartRow[] = series.points.map((p) => {
    const low = p.p25_eur_per_m2;
    const high = p.p75_eur_per_m2;
    return {
      period: p.period,
      year: parseInt(p.period.slice(0, 4), 10),
      mm4q: p.median_mm4q_eur_per_m2,
      communeMm4q: communeByPeriod.get(p.period) ?? null,
      p25: low,
      p75: high,
      bandLow: low,
      bandRange: low != null && high != null ? high - low : null,
      // Only surface the raw quarterly median for quarters that have ≥ 4 tx
      // (same threshold as IQR availability). Below that the raw median is
      // too noisy to plot responsibly.
      median: p.count >= 4 ? p.median_eur_per_m2 : null,
      count: p.count,
    };
  });

  const hasCommuneLine = rows.some((r) => r.communeMm4q != null);

  const maxCount = Math.max(1, ...rows.map((r) => r.count));
  // Detect the window where we actually have any MM4Q data — makes the line
  // look deliberate rather than "starting from zero".
  const mm4qRows = rows.filter((r) => r.mm4q != null);
  if (mm4qRows.length < 2) {
    return <EmptyState />;
  }

  // Year tick : show the first quarter of each year to keep axis readable.
  const yearTicks = rows
    .filter((r, i) => r.period.endsWith("Q1") || i === 0)
    .map((r) => r.period);

  return (
    <div className="space-y-2">
      <div className="h-56 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 10, right: 8, bottom: 0, left: -8 }}
          >
            <CartesianGrid stroke="#e5e7eb" strokeDasharray="2 3" vertical={false} />
            <XAxis
              dataKey="period"
              ticks={yearTicks}
              tickFormatter={(p: string) => p.slice(0, 4)}
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              interval={0}
              minTickGap={16}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#6b7280" }}
              tickLine={false}
              axisLine={{ stroke: "#e5e7eb" }}
              tickFormatter={(v: number) => formatEurShort(v)}
              width={52}
              domain={["auto", "auto"]}
            />
            <Tooltip content={<ChartTooltip communeName={communeName} />} />
            {/* IQR band: bottom = p25 (invisible), top = p75 - p25 on top */}
            <Area
              type="monotone"
              dataKey="bandLow"
              stackId="iqr"
              stroke="none"
              fill="transparent"
              isAnimationActive={false}
              legendType="none"
            />
            <Area
              type="monotone"
              dataKey="bandRange"
              stackId="iqr"
              stroke="none"
              fill="#84cc16"
              fillOpacity={0.18}
              isAnimationActive={false}
              legendType="none"
            />
            {hasCommuneLine && (
              <Line
                type="monotone"
                dataKey="communeMm4q"
                stroke="#6b7280"
                strokeWidth={1.5}
                strokeDasharray="4 3"
                dot={false}
                activeDot={{ r: 2.5, fill: "#6b7280" }}
                connectNulls
                isAnimationActive={false}
              />
            )}
            <Line
              type="monotone"
              dataKey="mm4q"
              stroke="#4d7c0f"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 3, fill: "#4d7c0f" }}
              connectNulls
              isAnimationActive={false}
            />
            <Scatter
              dataKey="median"
              fill="#4d7c0f"
              fillOpacity={0.55}
              shape="circle"
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Volume bandelette : barre par trimestre, même échelle x que le graphe.
          Ne réutilise pas ResponsiveContainer pour garder l'alignement propre. */}
      <div className="h-10 -mx-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={rows}
            margin={{ top: 2, right: 8, bottom: 0, left: -8 }}
          >
            <XAxis
              dataKey="period"
              hide
              ticks={yearTicks}
              interval={0}
            />
            <YAxis
              hide
              domain={[0, maxCount]}
              width={52}
            />
            <Tooltip content={<VolumeTooltip />} />
            <Bar
              dataKey="count"
              fill="#a3a3a3"
              opacity={0.6}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-500">
        <LegendSwatch color="#4d7c0f" label="Quartier — médiane lissée 4 trim." />
        {hasCommuneLine && (
          <LegendSwatch color="#6b7280" dashed label={`Commune · ${communeName}`} />
        )}
        <LegendSwatch color="#84cc16" opacity={0.18} solid label="Quartiles 25 – 75 %" />
        <LegendSwatch color="#a3a3a3" solid opacity={0.6} label="Volume de ventes" />
      </div>
    </div>
  );
}

function LegendSwatch({
  color,
  label,
  opacity,
  solid,
  dashed,
}: {
  color: string;
  label: string;
  opacity?: number;
  solid?: boolean;
  dashed?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0">
      {dashed ? (
        // Dashed swatch : inline SVG pour reproduire le trait pointillé du
        // graphe sans dépendre d'un background pattern.
        <svg
          width="14"
          height="4"
          viewBox="0 0 14 4"
          aria-hidden
          style={{ opacity: opacity ?? 1 }}
        >
          <line
            x1="0"
            y1="2"
            x2="14"
            y2="2"
            stroke={color}
            strokeWidth="1.5"
            strokeDasharray="3 2"
          />
        </svg>
      ) : (
        <span
          className={solid ? "w-2.5 h-2.5 rounded-sm" : "w-3 h-[2px] rounded"}
          style={{ background: color, opacity: opacity ?? 1 }}
        />
      )}
      <span className="truncate">{label}</span>
    </span>
  );
}

// ─── Tooltips ───────────────────────────────────────────────────────────

type TooltipPayload = {
  payload?: ChartRow;
};

function ChartTooltip({
  active,
  payload,
  communeName,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  communeName?: string;
}) {
  if (!active || !payload?.[0]?.payload) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-lg bg-ink-900/95 text-white px-3 py-2 text-[11.5px] shadow-lg">
      <div className="font-semibold">{formatQuarter(r.period)}</div>
      <div className="tabular mt-1">
        Quartier :{" "}
        <span className="text-lime-300">{formatEurPerM2(r.mm4q)}</span>
      </div>
      {r.communeMm4q != null && (
        <div className="tabular text-white/80">
          Commune{communeName ? ` · ${communeName}` : ""} :{" "}
          {formatEurPerM2(r.communeMm4q)}
        </div>
      )}
      {r.p25 != null && r.p75 != null && (
        <div className="tabular text-white/80">
          IQR 25–75 % : {formatEurShort(r.p25)} – {formatEurShort(r.p75)}
        </div>
      )}
      <div className="text-white/70 mt-1">
        {r.count} vente{r.count > 1 ? "s" : ""} ce trimestre
      </div>
    </div>
  );
}

function VolumeTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
}) {
  if (!active || !payload?.[0]?.payload) return null;
  const r = payload[0].payload;
  return (
    <div className="rounded-lg bg-ink-900/95 text-white px-2.5 py-1.5 text-[11px] shadow-lg">
      {formatQuarter(r.period)} — {r.count} vente{r.count > 1 ? "s" : ""}
    </div>
  );
}

// ─── Empty state ────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="rounded-lg bg-ink-50 hairline px-3 py-4 text-[12.5px] text-ink-600">
      Pas assez de transactions pour tracer une évolution fiable sur cette
      zone. DVF ne couvre pas l'Alsace-Moselle ; ailleurs, les rayons 500 m
      et 1 km n'ont pas réuni assez de ventes dans les 10 dernières années.
    </div>
  );
}

// ─── Formatters ─────────────────────────────────────────────────────────

function formatEurPerM2(v: number | null): string {
  if (v == null) return "—";
  return `${v.toLocaleString("fr-FR")} €/m²`;
}

function formatEurShort(v: number | null): string {
  if (v == null) return "—";
  if (v >= 10_000) return `${Math.round(v / 1000)} k`;
  return `${v.toLocaleString("fr-FR")}`;
}

function formatPctSigned(v: number | null): string {
  if (v == null) return "—";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toLocaleString("fr-FR", { maximumFractionDigits: 1 })} %`;
}

function formatQuarter(p: string): string {
  const y = p.slice(0, 4);
  const q = p.slice(5);
  return `T${q} ${y}`;
}

function toneOfPct(
  v: number | null,
): "pos" | "neg" | "neutral" | undefined {
  if (v == null) return undefined;
  if (v > 0.5) return "pos";
  if (v < -0.5) return "neg";
  return "neutral";
}

// ─── Small helpers ──────────────────────────────────────────────────────

function findSeries(
  data: PriceHistory,
  t: PricePropertyType,
): PriceHistorySeries | null {
  return data.series.find((s) => s.propertyType === t) ?? null;
}

function findCommuneSeries(
  data: PriceHistory,
  t: PricePropertyType,
): PriceHistorySeries | null {
  if (!data.communeSeries) return null;
  return data.communeSeries.find((s) => s.propertyType === t) ?? null;
}
